import CodeIcon from '@mui/icons-material/Code';
import {
  Card,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { lazy, Suspense } from 'react';

import {
  borderRadius,
  fontWeight,
  lineHeight,
  zIndex,
} from '@verantos-dev/shared-ui-components/tokens';

import { useTableShell } from '../hooks/useTableShell';
import { useTableSqlDrawer } from '../hooks/useTableSqlDrawer';
import type { TableShellLabel, TableShellProps } from '../types';
import { TableLinkButton } from './TableLinkButton';
import {
  getCharacteristicColumnWidthPx,
  getTableWidthPx,
  VALUE_COLUMN_WIDTH_PX,
} from '../utils/tableColumnWidths';

// Row and column lines use the theme's subtle border so a dense table stays quiet;
// only the pinned column's edge (below) is stronger.
const SUBTLE_BORDER_COLOR = 'border.subtle';

const DIVIDER_BOTTOM_BORDER_SX = {
  borderBottom: '1px solid',
  borderBottomColor: SUBTLE_BORDER_COLOR,
} as const;

// Pinned first column: stays visible while the remaining columns scroll horizontally.
// Its right border marks the edge the scrolling columns pass under; with the table's
// separate borders it stays on the cell as it sticks.
const PINNED_CELL_SX = {
  position: 'sticky',
  left: 0,
  zIndex: zIndex.sticky,
  bgcolor: 'background.paper',
  borderRight: '1px solid',
  borderRightColor: 'divider',
} as const;

const DIVIDER_CELL_BORDER_SX = {
  ...DIVIDER_BOTTOM_BORDER_SX,
  borderLeft: '1px solid',
  borderLeftColor: SUBTLE_BORDER_COLOR,
} as const;

// Value columns are divided from each other by a left border. The first one already
// has the pinned column's right border beside it, so it adds none of its own.
const valueColumnBorderSx = (columnIndex: number) =>
  columnIndex === 0 ? DIVIDER_BOTTOM_BORDER_SX : DIVIDER_CELL_BORDER_SX;

// The SQL drawer pulls in the syntax highlighter and formatter, so it loads on first open.
const TableSqlDrawer = lazy(() =>
  import('./TableSqlDrawer').then((module) => ({ default: module.TableSqlDrawer }))
);

const CELL_PADDING_Y = { body: 1.5, section: 2, header: 2.5 } as const;
const COMPACT_LINE_HEIGHT = `${lineHeight.normal}%`;
const COMPACT_TEXT_SX = { lineHeight: COMPACT_LINE_HEIGHT } as const;

export const TableShell = (props: TableShellProps) => {
  const { table } = props;
  const {
    title,
    characteristicLabel,
    noRowsLabel,
    columnCount,
    getSectionHeader,
    getRowLabel,
    getColumnHeader,
    getCell,
    previewAvId,
    onClosePreview,
    renderVariablePreview,
  } = useTableShell(props);
  const sqlDrawer = useTableSqlDrawer(props.definitionTable);

  // The table is as wide as its columns (a first column fitted to its longest label,
  // then a fixed width per value column), so values sit beside their labels. It
  // scrolls horizontally, with the first column pinned, once the card is narrower.
  const characteristicWidth = getCharacteristicColumnWidthPx([
    characteristicLabel,
    ...table.rows.flatMap((row) => (row.kind === 'row' ? [row.label] : [])),
  ]);
  const tableWidth = getTableWidthPx(characteristicWidth, table.columns.length);

  const renderLabel = ({ label, onClick, testId }: TableShellLabel) =>
    onClick ? (
      <TableLinkButton onClick={onClick} data-testid={testId}>
        {label}
      </TableLinkButton>
    ) : (
      label
    );

  return (
    <>
      <Card
        variant="outlined"
        data-testid="analysis-table-grid"
        sx={{
          borderRadius: `${borderRadius.md}px`,
          overflow: 'hidden',
          borderColor: SUBTLE_BORDER_COLOR,
          // Shrinks to the table, up to the width of the page.
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        {/* width 0 + minWidth 100%: the title takes the card's width and wraps within
            it, rather than widening the card to fit a long title on one line. */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 4, py: 3, width: 0, minWidth: '100%' }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: fontWeight.semibold, alignSelf: 'stretch' }}
          >
            {title}
          </Typography>
          {sqlDrawer.hasSql && (
            <Tooltip title={sqlDrawer.openLabel}>
              <IconButton
                size="small"
                aria-label={sqlDrawer.openLabel}
                onClick={sqlDrawer.onOpen}
                data-testid="analysis-table-sql-open"
                sx={{ ml: 'auto', flexShrink: 0 }}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Divider sx={{ borderColor: SUBTLE_BORDER_COLOR }} />
        <TableContainer>
          <Table
            size="small"
            data-testid="analysis-table-grid-table"
            // Fixed layout gives every column exactly its width; the table is the sum
            // of them, and the container scrolls horizontally when the card is narrower.
            // Separate borders (not MUI's collapsed default) keep a pinned cell's border
            // with it while the other columns scroll under.
            sx={{
              tableLayout: 'fixed',
              width: tableWidth,
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}
          >
            <TableHead
              sx={{
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    py: CELL_PADDING_Y.header,
                    width: characteristicWidth,
                    fontWeight: fontWeight.medium,
                    ...COMPACT_TEXT_SX,
                    ...PINNED_CELL_SX,
                    ...DIVIDER_BOTTOM_BORDER_SX,
                  }}
                >
                  {characteristicLabel}
                </TableCell>
                {table.columns.map((column, columnIndex) => {
                  const columnHeader = getColumnHeader(column);
                  return (
                    <TableCell
                      key={column.columnId}
                      align="left"
                      sx={{
                        py: CELL_PADDING_Y.header,
                        width: VALUE_COLUMN_WIDTH_PX,
                        ...valueColumnBorderSx(columnIndex),
                      }}
                    >
                      <Stack alignItems="flex-start">
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: fontWeight.medium,
                            ...COMPACT_TEXT_SX,
                          }}
                          // Blue marks a link; a header with nothing to open stays plain.
                          color={columnHeader.onClick ? 'primary' : 'text.primary'}
                        >
                          {renderLabel(columnHeader)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ lineHeight: COMPACT_LINE_HEIGHT }}
                        >
                          {column.intent ?? ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
            >
              {table.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ py: CELL_PADDING_Y.body }}>
                    <Typography variant="body2" color="text.secondary" sx={COMPACT_TEXT_SX}>
                      {noRowsLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                table.rows.map((row, index) =>
                  row.kind === 'section_header' ? (
                    <TableRow key={`section-header-${index}`}>
                      <TableCell
                        colSpan={columnCount}
                        data-testid={`analysis-table-grid-section-${index}`}
                        sx={{
                          // A header that owns a variable reads as a variable row
                          // (regular weight); only a pure grouping header is bold.
                          fontWeight: row.uses.length > 0 ? fontWeight.regular : fontWeight.medium,
                          py: CELL_PADDING_Y.section,
                          ...COMPACT_TEXT_SX,
                          borderBottomColor: SUBTLE_BORDER_COLOR,
                        }}
                      >
                        {renderLabel(getSectionHeader(row))}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.rowId} data-testid={`analysis-table-grid-row-${row.rowId}`}>
                      <TableCell
                        sx={{
                          py: CELL_PADDING_Y.body,
                          ...COMPACT_TEXT_SX,
                          ...PINNED_CELL_SX,
                          ...DIVIDER_BOTTOM_BORDER_SX,
                        }}
                      >
                        {renderLabel(getRowLabel(row))}
                      </TableCell>
                      {table.columns.map((column, columnIndex) => {
                        const { value, isPopulated } = getCell(row, column);
                        return (
                          <TableCell
                            key={column.columnId}
                            align="left"
                            data-testid={`analysis-table-grid-cell-${row.rowId}-${column.columnId}`}
                            sx={{
                              py: CELL_PADDING_Y.body,
                              color: 'text.secondary',
                              ...valueColumnBorderSx(columnIndex),
                            }}
                          >
                            <Typography
                              variant="body2"
                              color={isPopulated ? 'text.primary' : 'inherit'}
                              sx={{ overflowWrap: 'anywhere', ...COMPACT_TEXT_SX }}
                            >
                              {value}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {renderVariablePreview?.({ avId: previewAvId, onClose: onClosePreview })}
      {sqlDrawer.isOpen && (
        <Suspense fallback={null}>
          <TableSqlDrawer
            title={title}
            heading={sqlDrawer.heading}
            columnsHeading={sqlDrawer.columnsHeading}
            rowsHeading={sqlDrawer.rowsHeading}
            copyLabel={sqlDrawer.copyLabel}
            copiedLabel={sqlDrawer.copiedLabel}
            closeLabel={sqlDrawer.closeLabel}
            entries={sqlDrawer.entries}
            onClose={sqlDrawer.onClose}
          />
        </Suspense>
      )}
    </>
  );
};
